import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import type {
  ApiError,
  AuthStatus,
  LocationDetail,
  LocationListItem,
  PaginatedResponse,
  Review,
  SavedLocation,
  SearchResult,
  TaxonomyTerm,
} from './types'

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/$/, '')
export const DISCOURSE_URL = (import.meta.env.VITE_DISCOURSE_URL || 'https://piknik.spletnimojster.si/forum').replace(/\/$/, '')
export const RELEASE_SHA = (import.meta.env.VITE_RELEASE_SHA || 'dev').slice(0, 7)

function getCookie(name: string): string | undefined {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift()
  }
  return undefined
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers ?? {})
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const method = init?.method?.toUpperCase() ?? 'GET'
  if (!['GET', 'HEAD', 'OPTIONS', 'TRACE'].includes(method)) {
    const csrfToken = getCookie('csrftoken')
    if (csrfToken) {
      headers.set('X-CSRFToken', csrfToken)
    }
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers,
  })

  const payload = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw payload satisfies ApiError
  }

  return payload as T
}

export function formatApiError(error: unknown) {
  if (!error || typeof error !== 'object') {
    return 'Request failed.'
  }

  const apiError = error as ApiError
  if (apiError.message) {
    return apiError.message
  }

  if (apiError.fields) {
    return Object.entries(apiError.fields)
      .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
      .join(' · ')
  }

  return 'Request failed.'
}

export function useAuthStatus() {
  return useQuery({
    queryKey: ['auth-status'],
    queryFn: () => apiFetch<AuthStatus>('/auth/status'),
    retry: false,
  })
}

export function useTaxonomy(type?: string) {
  const suffix = type ? `?type=${encodeURIComponent(type)}` : ''
  return useQuery({
    queryKey: ['taxonomy', type ?? 'all'],
    queryFn: () => apiFetch<PaginatedResponse<TaxonomyTerm>>(`/taxonomy${suffix}`),
  })
}

export function useLocations(filters: URLSearchParams) {
  const queryString = filters.toString()
  return useInfiniteQuery({
    queryKey: ['locations', queryString],
    queryFn: ({ pageParam = 1 }) =>
      apiFetch<PaginatedResponse<LocationListItem>>(
        `/locations?page=${pageParam}${queryString ? `&${queryString}` : ''}`,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.next) {
        return pages.length + 1
      }
      return undefined
    },
  })
}

export function useLocation(slug?: string) {
  return useQuery({
    queryKey: ['location', slug],
    enabled: Boolean(slug),
    queryFn: () => apiFetch<LocationDetail>(`/locations/${slug}`),
  })
}

export function useReviews(locationId?: string) {
  return useQuery({
    queryKey: ['reviews', locationId],
    enabled: Boolean(locationId),
    queryFn: () => apiFetch<PaginatedResponse<Review>>(`/reviews?location=${locationId}`),
  })
}

export function useSavedLocations(enabled = true) {
  return useQuery({
    queryKey: ['saved-locations'],
    enabled,
    retry: false,
    queryFn: () => apiFetch<PaginatedResponse<SavedLocation>>('/saved-locations'),
  })
}

export function useSearch(query: string, region?: string) {
  const trimmedQuery = query.trim()
  const searchParams = new URLSearchParams()
  if (trimmedQuery) searchParams.set('q', trimmedQuery)
  if (region) searchParams.set('region', region)

  return useQuery({
    queryKey: ['search', trimmedQuery, region ?? ''],
    enabled: Boolean(trimmedQuery),
    queryFn: () => apiFetch<PaginatedResponse<SearchResult>>(`/search?${searchParams.toString()}`),
  })
}
